import { useRef, useMemo, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations, Html } from "@react-three/drei";
import * as THREE from "three";
import { clone as skeletonClone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { Player } from "../../types";
import { setAvatarAnchor, clearAvatarAnchor } from "./avatarAnchors";
import {
  UNITS_PER_STEP,
  LANE_SPACING,
  WALK_THRESHOLD,
  RUN_THRESHOLD,
  MODEL_SCALE,
  PLAYER_COLORS,
  SPRING_STIFFNESS,
  SPRING_DAMPING,
  PODIUM_MODEL_YAW,
} from "./constants";

interface PlayerCharacterProps {
  player: Player;
  laneIndex: number;
  totalLanes: number;
  rank: number;
  isCurrentPlayer: boolean;
  avatarFile: string;
  isWinner?: boolean;
  isFinished?: boolean;
  showLabel?: boolean;
  // Position monde imposée en fin de partie (place sur le podium). Quand elle est
  // fournie, l'avatar y est posé sec : la téléportation est masquée par le flash
  // blanc de ThreeGame, donc aucun tween n'est nécessaire ni souhaitable.
  podiumTarget?: [number, number, number] | null;
  // Phase podium : change l'orientation du modèle (face caméra au lieu de face piste).
  onPodium?: boolean;
}

// Recherche fuzzy d'animation par nom (insensible à la casse)
function findAnim(names: string[], pattern: RegExp): string | undefined {
  return names.find((n) => pattern.test(n));
}

export function PlayerCharacter({
  player,
  laneIndex,
  totalLanes,
  rank,
  isCurrentPlayer,
  avatarFile,
  isWinner = false,
  isFinished = false,
  showLabel = true,
  podiumTarget = null,
  onPodium = false,
}: PlayerCharacterProps) {
  const groupRef = useRef<THREE.Group>(null!);
  const modelRef = useRef<THREE.Group>(null!);
  const currentAnimRef = useRef<string>("idle");
  // Spring-based tween : position interpolée + vélocité pour un mouvement fluide
  const smoothX = useRef((player.position || 0) * UNITS_PER_STEP);
  const velocityX = useRef(0);

  // Charger le GLB et cloner avec SkeletonUtils (indépendance des squelettes)
  const { scene, animations } = useGLTF(avatarFile);
  const clonedScene = useMemo(() => {
    const clone = skeletonClone(scene);
    clone.traverse((child: THREE.Object3D) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        // Désactiver le frustum culling — sinon les skinned meshes disparaissent quand la caméra s'éloigne
        child.frustumCulled = false;
      }
    });
    return clone;
  }, [scene]);

  // useAnimations bindé sur modelRef (le group qui contient directement le clone)
  const { actions, names } = useAnimations(animations, modelRef);

  // Trouver les animations disponibles
  const animNames = useMemo(
    () => ({
      idle: findAnim(names, /idle/i) || names[0],
      walk: findAnim(names, /walk/i),
      run: findAnim(names, /run/i),
      dance: findAnim(names, /dance/i) || findAnim(names, /gesture.?positive/i),
    }),
    [names]
  );

  // Lancer l'animation idle au montage
  useEffect(() => {
    const idleAction = animNames.idle && actions[animNames.idle];
    if (idleAction) {
      idleAction.reset().fadeIn(0.2).play();
      currentAnimRef.current = "idle";
    }
  }, [actions, animNames.idle]);

  // Joueur qui a fini ses questions OU winner : jouer l'animation dance en boucle
  const shouldDance = isWinner || isFinished || !!podiumTarget;
  useEffect(() => {
    if (!shouldDance) return;
    const danceName = animNames.dance;
    if (!danceName || !actions[danceName]) return;
    // Stopper l'anim courante et lancer la dance
    const prevClipName = animNames[currentAnimRef.current as keyof typeof animNames];
    const prevAction = prevClipName ? actions[prevClipName] : null;
    if (prevAction) prevAction.fadeOut(0.3);
    actions[danceName]!.reset().fadeIn(0.3).setLoop(THREE.LoopRepeat, Infinity).play();
    currentAnimRef.current = "dance";
  }, [shouldDance, actions, animNames]);

  // Position Z de la lane (centrée autour de Z=0)
  const lanesOffset = ((totalLanes - 1) * LANE_SPACING) / 2;
  const laneZ = -laneIndex * LANE_SPACING + lanesOffset;

  // Couleur du joueur
  const playerColor = PLAYER_COLORS[laneIndex % PLAYER_COLORS.length];
  const rankColor =
    rank === 1
      ? "#FFD700"
      : rank === 2
        ? "#C0C0C0"
        : rank === 3
          ? "#CD7F32"
          : "#AAAAAA";

  // Transition d'animation fluide
  const playAnim = (name: "idle" | "walk" | "run" | "dance") => {
    if (currentAnimRef.current === name) return;
    const clipName = animNames[name];
    if (!clipName || !actions[clipName]) return;

    const prevClipName = animNames[currentAnimRef.current as keyof typeof animNames];
    const prevAction = prevClipName ? actions[prevClipName] : null;
    const nextAction = actions[clipName];

    if (nextAction) {
      nextAction.reset().fadeIn(0.25).play();
      if (prevAction) prevAction.fadeOut(0.25);
      currentAnimRef.current = name;
    }
  };

  // Nettoyage du registre quand le joueur quitte la scene.
  useEffect(() => () => clearAvatarAnchor(player.id), [player.id]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;

    // Publie la position REELLEMENT rendue (sortie du ressort), avant tout
    // early-return : les joueurs qui dansent ou sont sur le podium doivent aussi
    // garder un point d'attache a jour. Cf. avatarAnchors.ts.
    setAvatarAnchor(player.id, groupRef.current.position);

    // Podium : position imposée, on court-circuite le spring. Doit passer AVANT
    // le early-return shouldDance, sinon les joueurs qui dansent déjà (ils ont
    // tous fini à ce stade) resteraient figés sur la piste.
    if (podiumTarget) {
      groupRef.current.position.set(podiumTarget[0], podiumTarget[1], podiumTarget[2]);
      return;
    }

    // Joueur qui danse (fini ou winner) : on freeze le mouvement
    if (shouldDance) return;

    const targetX = (player.position || 0) * UNITS_PER_STEP;

    // Spring-damper system pour un tween fluide (pas de téléportation)
    const displacement = smoothX.current - targetX;
    const springForce = -SPRING_STIFFNESS * displacement;
    const dampForce = -SPRING_DAMPING * velocityX.current;
    velocityX.current += (springForce + dampForce) * delta;
    smoothX.current += velocityX.current * delta;

    groupRef.current.position.x = smoothX.current;
    groupRef.current.position.z = laneZ;

    // Machine à états d'animation basée sur la vélocité du spring
    const speed = Math.abs(velocityX.current);
    if (speed > RUN_THRESHOLD) {
      playAnim("run");
    } else if (speed > WALK_THRESHOLD) {
      playAnim(animNames.run ? "walk" : "run");
    } else {
      playAnim("idle");
    }
  });

  return (
    <group
      ref={groupRef}
      position={[
        (player.position || 0) * UNITS_PER_STEP,
        0,
        laneZ,
      ]}
    >
      {/* Modèle 3D — rotation Y de 90° pour orienter face à la direction de la piste (+X).
          Sur le podium les places sont alignées sur X et la photo se prend depuis +Z :
          on remet le modèle face caméra, sinon les avatars se regardent le dos. */}
      <group
        ref={modelRef}
        scale={MODEL_SCALE}
        rotation={[0, onPodium ? PODIUM_MODEL_YAW : Math.PI / 2, 0]}
      >
        <primitive object={clonedScene} />
      </group>

      {/* Label nom + rang derrière le personnage — masqué en mode joueur mobile */}
      {/* Sur la piste le label est décalé DERRIÈRE l'avatar (il court vers +X, le
          label ne doit pas masquer ce qui arrive). Sur le podium les avatars sont
          côte à côte : un label décalé atterrit sur le voisin, donc on le centre
          au-dessus de la tête. */}
      {showLabel && <Html
        position={onPodium ? [0, 2.6, 0] : [-1.5, 1, 0]}
        center={onPodium}
        style={{
          pointerEvents: "none",
          transform: onPodium ? undefined : "translateX(-100%)",
        }}
        zIndexRange={[0, 0]}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              fontSize: "16px",
              fontWeight: 800,
              color: rankColor,
              backgroundColor: "rgba(0,0,0,0.6)",
              padding: "2px 10px",
              borderRadius: "10px",
              fontFamily: "Nunito, sans-serif",
            }}
          >
            #{rank}
          </span>
          <span
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#fff",
              backgroundColor: playerColor,
              padding: "4px 12px",
              borderRadius: "12px",
              fontFamily: "Nunito, sans-serif",
              border: isCurrentPlayer ? "2px solid #fff" : "none",
            }}
          >
            {player.name}
          </span>
        </div>
      </Html>}
    </group>
  );
}
